import {useMemo} from 'react'
import {FormProvider, useFieldArray, useForm, useWatch} from 'react-hook-form'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {Button} from '@/components/ui/button/Button'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {splitDiagnosisPair} from '@/features/projects/diagnosisVersion'
import {OtherPathologiesFormSchema, ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {DEFAULT_OTHER_PATHOLOGY_VALUES} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {zodResolver} from '@hookform/resolvers/zod'
import {useStoreProjectOtherPathologies} from '@/features/projects/queries/useStoreProjectOtherPathologies'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {ProjectOtherPathologiesFields} from '@/features/projects/components/project-other-pathologies-fields/ProjectOtherPathologiesFields'
import {ObjectKeys} from '@/utilities/helpers'

interface ProjectOtherPathologiesFormProps {
    projectId: string
    defaultValues?: ProjectFieldsSchema['otherPathologies']
    isDisabled: boolean
    isEditModeActive: boolean
    handleCancel: () => void
    handleEdit: () => void
}

export const ProjectOtherPathologiesForm = ({
    projectId,
    defaultValues,
    isDisabled,
    isEditModeActive,
    handleCancel,
    handleEdit
}: ProjectOtherPathologiesFormProps) => {
    const {t} = useTranslation()
    const form = useForm<OtherPathologiesFormSchema>({
        resolver: zodResolver(OtherPathologiesFormSchema),
        defaultValues: {
            otherPathologies: defaultValues || [DEFAULT_OTHER_PATHOLOGY_VALUES]
        }
    })

    const {fields, append, remove} = useFieldArray({
        control: form.control,
        name: 'otherPathologies'
    })

    const {mutate: storeProjectOtherPathologies, isPending} = useStoreProjectOtherPathologies({
        urlParams: {
            projectId
        },
        options: {
            onSuccess: async () => {
                await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT_OTHER_PATHOLOGIES, projectId]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_OTHER_PATHOLOGIES, projectId]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, projectId]})
                handleCancel()
            }
        }
    })

    // See the main pathologies form: `fields` would not reflect codes picked during this session.
    const pathologies = useWatch({control: form.control, name: 'otherPathologies'})
    const pairs = useMemo(() => pathologies?.map(({diagnosis}) => splitDiagnosisPair(diagnosis)) ?? [], [pathologies])
    const disabledIcd9 = useMemo(() => pairs.flatMap(({icd9}) => icd9?.id ?? []), [pairs])
    const disabledIcd10 = useMemo(() => pairs.flatMap(({icd10}) => icd10?.id ?? []), [pairs])

    const onSubmit = form.handleSubmit(
        data =>
            storeProjectOtherPathologies({
                otherPathologies: data.otherPathologies.map(({diagnosis}) => ({diagnosisId: diagnosis?.id ?? ''}))
            }),
        errors => console.error(errors)
    )

    return (
        <FormProvider {...form}>
            <DetailsFieldsSection
                variant="neutral"
                title={t('projects:other_pathologies_card_title')}
                isDisabled={isDisabled}
                headingEndSlot={
                    <EditDetailsSectionButtons
                        isLoading={isPending}
                        isActive={isEditModeActive}
                        onCancel={handleCancel}
                        onEdit={handleEdit}
                        onConfirm={onSubmit}
                        isDisabled={!form.formState.isValid || !ObjectKeys(form.formState.dirtyFields).length}
                    />
                }
            >
                <Flexbox direction="column" gap={6} fullWidth>
                    {fields.map((field, index) => (
                        <ProjectOtherPathologiesFields
                            key={field.id}
                            index={index}
                            disabledIcd9={disabledIcd9}
                            disabledIcd10={disabledIcd10}
                            showTrashButton={fields.length > 1}
                            remove={remove}
                        />
                    ))}
                    {fields.length < 3 && (
                        <Button variant="secondary" size="sm" onClick={() => append(DEFAULT_OTHER_PATHOLOGY_VALUES)}>
                            <PlusIcon />
                            {t('projects:add_pathology')}
                        </Button>
                    )}
                </Flexbox>
            </DetailsFieldsSection>
        </FormProvider>
    )
}
